import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PlanningLens } from 'pages/FarmsPage/hooks/resolvePlanningLens';
import {
  ANALYSIS_GROUP_BY_CROP_VARIETY,
  ANALYSIS_GROUP_BY_FARM,
  ANALYSIS_GROUP_BY_NONE,
  ANALYSIS_PRICE_EXPECTED,
  ANALYSIS_PRICE_OPTIMISTIC,
  ANALYSIS_PRICE_PESSIMISTIC,
  ANALYSIS_PRODUCTION_EXPECTED,
  ANALYSIS_PRODUCTION_LOW,
  ANALYSIS_PRODUCTION_POTENTIAL,
  ANALYSIS_SUMMARY_AVERAGE,
  ANALYSIS_SUMMARY_TOTAL,
  CASHFLOW_DETAIL_CATEGORIES,
  CASHFLOW_DETAIL_PRODUCTS,
  CASHFLOW_PERIOD_MONTH,
  CASHFLOW_PERIOD_QUARTER,
  CASHFLOW_YEAR_CALENDAR,
  CASHFLOW_YEAR_FINANCIAL,
  FARM_DIAGNOSTICS_LENS_BIO_VS_FINANCIAL,
  FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY,
  FARM_DIAGNOSTICS_LENS_ROTATION_EFFICIENCY,
  FARM_DIAGNOSTICS_LENS_WUE_TREND,
  FARM_DIAGNOSTICS_PRACTICE_CONVENTIONAL_TILL,
  FARM_DIAGNOSTICS_PRACTICE_COVER_CROP,
  FARM_DIAGNOSTICS_PRACTICE_FALLOW,
  FARM_DIAGNOSTICS_PRACTICE_NO_TILL,
  FARM_DIAGNOSTICS_PRACTICE_PASTURE,
  FARM_DIAGNOSTICS_TIME_RANGE_ALL,
  FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_3,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_5,
  INPUT_DOC_LENS_ANALYSIS,
  INPUT_DOC_LENS_CASHFLOW,
  INPUT_DOC_LENS_MARGIN_BREAK_EVEN,
  INPUT_DOC_LENS_NUTRITION,
  INPUT_DOC_LENS_ROTATION,
  INPUT_DOC_LENS_SUMMARY,
  INPUT_DOC_LENS_SUMMARY_2,
  INPUT_DOC_TAB_DIAGNOSTICS,
  INPUT_DOC_TAB_INSIGHTS,
  INPUT_DOC_TAB_MAP,
  INPUT_DOC_TAB_PLANNING,
  NUTRITION_SEASON_RANGE_THIS_YEAR,
  NUTRITION_FARM_VIEW_BALANCE,
  NUTRITION_GROUP_BY_CROP,
  NUTRITION_GROUP_BY_FARM,
  NUTRITION_FARM_VIEW_SEASONS,
  RIGHT_PANEL_STATE_STORAGE_KEY,
  SUMMARY_INPUT_VIEW_CHART,
  SUMMARY_INPUT_VIEW_STORAGE_KEY,
  SUMMARY_INPUT_VIEW_TABLE,
  SUMMARY_SCOPE_HINT_STORAGE_KEY,
  RIGHT_PANEL_TAB_CATEGORY_SPEND,
  RIGHT_PANEL_TAB_FINANCIAL,
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TAB_QUANTITY,
  RIGHT_PANEL_TAB_RESISTANCE,
  RIGHT_PANEL_TOOL_CALCULATORS,
  RIGHT_PANEL_TOOL_LEGACY_LIME_MATE,
  RIGHT_PANEL_TOOL_LIVE_TOTALS,
  SUMMARY_DATA_BASIS_ACTUAL,
  SUMMARY_DATA_BASIS_PLAN,
  SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL,
  SUMMARY_ENTITY_ALL,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_ENTITY_OPERATION,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_PRODUCTION,
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SEED_DRESS,
  SUMMARY_METRIC_COST_PER_HA,
  SUMMARY_METRIC_QUANTITY,
  SUMMARY_METRIC_TOTAL_AREA,
  SUMMARY_METRIC_TOTAL_COST,
  SUMMARY_TEMPORAL_FLAT,
  SUMMARY_TEMPORAL_MONTHLY,
} from 'constants/uiConstants';
import { InputTaskCategory } from 'interfaces/IInputTask';
import {
  CalculatorId,
  DEFAULT_CALCULATOR_ID,
  isCalculatorId,
} from 'utils/calculators';
import type { NutrientKey } from 'pages/FarmsPage/Workspace/Insights/computeLiveTotals';
import type { NutritionIntent } from 'pages/FarmsPage/Workspace/Insights/nutrition/nutritionIntents';
import { loadFarmYearInputs } from 'pages/FarmsPage/Workspace/FarmDiagnostics/farmYearInputsStorage';

export type InsightsTab =
  | typeof INPUT_DOC_TAB_PLANNING
  | typeof INPUT_DOC_TAB_INSIGHTS
  | typeof INPUT_DOC_TAB_DIAGNOSTICS
  | typeof INPUT_DOC_TAB_MAP;

export type InsightsLens =
  | typeof INPUT_DOC_LENS_SUMMARY
  | typeof INPUT_DOC_LENS_SUMMARY_2
  | typeof INPUT_DOC_LENS_MARGIN_BREAK_EVEN
  | typeof INPUT_DOC_LENS_ANALYSIS
  | typeof INPUT_DOC_LENS_CASHFLOW
  | typeof INPUT_DOC_LENS_NUTRITION
  | typeof INPUT_DOC_LENS_ROTATION;

/** Which details Rotation planner cells show under the crop (OP-2501). */
export interface RotationCellDetails {
  area: boolean;
  variety: boolean;
  production: boolean;
  sowing: boolean;
}

/** Nutrition lens season range, ending at the tree year (OP-2508). */
export type NutritionSeasonRange =
  | typeof NUTRITION_SEASON_RANGE_THIS_YEAR
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_LAST_3
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_LAST_5
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_ALL;

/** A Nutrition rate change that didn't save — enough to describe and resend it. */
export interface NutritionFailedSave {
  /** The change: the product line (inputId:taskId:stepId) for a rate, otherwise a change id. */
  key: string;
  /** What's still to send — rebuilt against the latest plans on retry (OP-2523). */
  intents: NutritionIntent[];
  /** For the user, e.g. "Urea on North paddock, 2025". */
  description: string;
  reason: string;
}

export interface NutritionSaveState {
  pendingCount: number;
  hasSaved: boolean;
  failed: NutritionFailedSave[];
  retryRequest: number;
}

/** Nutrition lens farm/org grid rows — paddocks grouped by farm, or plans grouped by crop (OP-2523). */
export type NutritionGroupBy =
  | typeof NUTRITION_GROUP_BY_FARM
  | typeof NUTRITION_GROUP_BY_CROP;

/** Nutrition lens farm/org grid — every nutrient's balance over the range, or one nutrient season by season (OP-2508). */
export type NutritionFarmView =
  | typeof NUTRITION_FARM_VIEW_BALANCE
  | typeof NUTRITION_FARM_VIEW_SEASONS;

/** Sub-dashboard shown inside the Farm Diagnostics tab. */
export type FarmDiagnosticsLens =
  | typeof FARM_DIAGNOSTICS_LENS_ROTATION_EFFICIENCY
  | typeof FARM_DIAGNOSTICS_LENS_WUE_TREND
  | typeof FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY
  | typeof FARM_DIAGNOSTICS_LENS_BIO_VS_FINANCIAL;

export type SummaryEntityCategory =
  | typeof SUMMARY_ENTITY_ALL
  | typeof SUMMARY_ENTITY_SEED
  | typeof SUMMARY_ENTITY_SOIL_INPUT
  | typeof SUMMARY_ENTITY_PLANT_PROTECTION
  | typeof SUMMARY_ENTITY_SEED_DRESS
  | typeof SUMMARY_ENTITY_BIOSTIMULANT
  | typeof SUMMARY_ENTITY_OPERATION
  | typeof SUMMARY_ENTITY_PRODUCTION;

export type SummaryDataMetric =
  | typeof SUMMARY_METRIC_QUANTITY
  | typeof SUMMARY_METRIC_TOTAL_AREA
  | typeof SUMMARY_METRIC_TOTAL_COST
  | typeof SUMMARY_METRIC_COST_PER_HA;

export type SummaryTemporalLayout =
  | typeof SUMMARY_TEMPORAL_FLAT
  | typeof SUMMARY_TEMPORAL_MONTHLY;

/** Which task category every basis-aware Summary section reads — one selector for the whole lens (OP-2387). */
export type SummaryDataBasis =
  | typeof SUMMARY_DATA_BASIS_PLAN
  | typeof SUMMARY_DATA_BASIS_ACTUAL
  | typeof SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL;

export type AnalysisPriceScenario =
  | typeof ANALYSIS_PRICE_PESSIMISTIC
  | typeof ANALYSIS_PRICE_EXPECTED
  | typeof ANALYSIS_PRICE_OPTIMISTIC;

export type AnalysisProductionScenario =
  | typeof ANALYSIS_PRODUCTION_LOW
  | typeof ANALYSIS_PRODUCTION_EXPECTED
  | typeof ANALYSIS_PRODUCTION_POTENTIAL;

export type AnalysisSummaryMode =
  | typeof ANALYSIS_SUMMARY_TOTAL
  | typeof ANALYSIS_SUMMARY_AVERAGE;

export type AnalysisGroupBy =
  | typeof ANALYSIS_GROUP_BY_NONE
  | typeof ANALYSIS_GROUP_BY_FARM
  | typeof ANALYSIS_GROUP_BY_CROP_VARIETY;

export type CashflowPeriod =
  | typeof CASHFLOW_PERIOD_MONTH
  | typeof CASHFLOW_PERIOD_QUARTER;

export type CashflowYearMode =
  | typeof CASHFLOW_YEAR_CALENDAR
  | typeof CASHFLOW_YEAR_FINANCIAL;

export type CashflowDetailLevel =
  | typeof CASHFLOW_DETAIL_CATEGORIES
  | typeof CASHFLOW_DETAIL_PRODUCTS;

/** Every Analysis grid column that can be individually shown/hidden — Paddock is always shown (row identifier). */
export type AnalysisColumnKey =
  | 'farmName'
  | 'areaHa'
  | 'crop'
  | 'variety'
  | 'startDate'
  | 'price'
  | 'yield'
  | 'production'
  | 'income'
  | SummaryEntityCategory
  | 'totalCosts'
  | 'grossMargin'
  | 'costPercentIncome';

// ── Farm Diagnostics types ──────────────────────────────────────────────────

/**
 * A user's session-only override of one paddock-year's Epic 1 figures —
 * never persisted (no localStorage, no backend write), cleared on refresh.
 * `costOverrides` replaces a category's $ total outright.
 */
export interface FarmDiagnosticsYearOverride {
  yieldOverride?: number;
  costOverrides?: Partial<Record<SummaryEntityCategory, number>>;
}

/**
 * Fixed, controlled set of management-practice values — never free text (a
 * new tag would silently break Dashboard 4's legend/correlation logic,
 * which keys off exact tag values). Fallow and Pasture are mutually
 * exclusive with each other and with every active-cropping practice — see
 * `resolvePracticeTagsAfterChange` in computeFarmDiagnosticsMetrics.ts.
 */
export type PracticeTag =
  | typeof FARM_DIAGNOSTICS_PRACTICE_NO_TILL
  | typeof FARM_DIAGNOSTICS_PRACTICE_CONVENTIONAL_TILL
  | typeof FARM_DIAGNOSTICS_PRACTICE_PASTURE
  | typeof FARM_DIAGNOSTICS_PRACTICE_FALLOW
  | typeof FARM_DIAGNOSTICS_PRACTICE_COVER_CROP;

/**
 * Rainfall + management-practice tags for one farm-year — farm-level, not
 * paddock-level (the same farm can have several paddocks selected in turn
 * without re-entering this). Remembered in this browser (OP-2528) — see
 * farmYearInputsStorage.ts.
 */
export interface FarmYearInput {
  rainfallMm: number | null;
  practiceTags: PracticeTag[];
}

/** Which window of years a Farm Diagnostics lens shows — the shape used by both `farmDiagnosticsTimeRange` (the three per-paddock lenses) and `rotationTimeRange` (Rotation Efficiency), kept independent of each other. */
export type FarmDiagnosticsTimeRangeMode =
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_LAST_3
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_LAST_5
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_ALL
  | typeof FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM;

export interface FarmDiagnosticsTimeRange {
  mode: FarmDiagnosticsTimeRangeMode;
  /** Only meaningful when mode is 'custom' — retained (not cleared) when switching to another mode, so Custom restores its prior selection when picked again. */
  customStartYear: number | null;
  customEndYear: number | null;
}

/** The Yearly Overview state the plan preview reproduces — what the user entered or selected, never anything fetched (OP-2490). */
export type FarmDiagnosticsSnapshot = Pick<
  InsightsState,
  | 'farmYearInputs'
  | 'farmDiagnosticsOverrides'
  | 'farmDiagnosticsCategory'
  | 'farmDiagnosticsTimeRange'
  | 'rotationTimeRange'
>;

// ── Right Panel types ────────────────────────────────────────────────────────

/** Floating panel position and size — all values in rem. */
export interface FloatingGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type RightPanelTabId =
  | typeof RIGHT_PANEL_TAB_NUTRITION
  | typeof RIGHT_PANEL_TAB_FINANCIAL
  | typeof RIGHT_PANEL_TAB_CATEGORY_SPEND
  | typeof RIGHT_PANEL_TAB_QUANTITY
  | typeof RIGHT_PANEL_TAB_RESISTANCE;

export type RightPanelTabStatus = 'collapsed' | 'floating' | 'pinned';

/**
 * What the pinned panel shows — the live totals tabs or the calculators
 * (OP-2383, OP-2585).
 */
export type RightPanelTool =
  | typeof RIGHT_PANEL_TOOL_LIVE_TOTALS
  | typeof RIGHT_PANEL_TOOL_CALCULATORS;

export interface RightPanelState {
  /** Per-tab status. */
  tabs: Record<RightPanelTabId, RightPanelTabStatus>;
  /** Display order for edge labels, tab control bar, and merge sections. */
  tabOrder: RightPanelTabId[];
  /**
   * Tabs currently shown as floating overlays.
   * Multiple tabs can float simultaneously. Not persisted to localStorage.
   */
  floatingTabIds: RightPanelTabId[];
  /**
   * Active tab in the pinned panel when 2+ tabs are pinned and not merged.
   * Ignored when isMerged is true.
   */
  activePinnedTabId: RightPanelTabId | null;
  /** When true, all pinned tabs appear as sections in one merged panel. */
  isMerged: boolean;
  /** Expand/collapse state per section in merge mode. */
  mergedSectionExpanded: Record<RightPanelTabId, boolean>;
  /**
   * Last known geometry (position + size, all in rem) for each floating tab.
   * Persisted so the panel reopens where the user left it.
   * null means the tab has never been floated; a default is used instead.
   */
  floatingGeometry: Record<RightPanelTabId, FloatingGeometry | null>;
  /**
   * Whether the chart section is visible in each tab.
   * Defaults to true. Persisted to localStorage.
   */
  chartVisible: Record<RightPanelTabId, boolean>;
  /**
   * Shared Plan/Actual selection used by every tab's narrow-width single-column
   * fallback (OP-2333) — one control at the top of the panel (RightPinnedPanel/
   * RightFloatingPanel header) rather than a separate toggle duplicated inside
   * each section. Defaults to Plan. Persisted to localStorage.
   */
  categoryFilter: InputTaskCategory;
  /**
   * Which tool the pinned panel shows (OP-2383). Live totals is the tab
   * system above; Calculators replaces the content area with a calculator and
   * keeps the pinned panel open even when no live totals tab is pinned.
   * Defaults to live totals. Persisted to localStorage.
   */
  activeTool: RightPanelTool;
  /**
   * The calculator the Calculators tool shows (OP-2585) — the last one
   * chosen. Defaults to the first calculator. Persisted to localStorage.
   */
  activeCalculator: CalculatorId;
  /**
   * Snapshot of tabs/activePinnedTabId/floatingTabIds taken the moment the
   * Analysis lens becomes active (OP-2333 — Analysis already covers the same
   * cost/rate figures as the Live Panel, so switching to it force-collapses
   * the panel). Restored on leaving Analysis only if the user made no manual
   * pin/float changes while there — otherwise their explicit choice wins.
   * Not persisted; always null on page load.
   */
  analysisSnapshot: {
    tabs: Record<RightPanelTabId, RightPanelTabStatus>;
    activePinnedTabId: RightPanelTabId | null;
    floatingTabIds: RightPanelTabId[];
  } | null;
}

// ── Default / initial values ─────────────────────────────────────────────────

const DEFAULT_TAB_ORDER: RightPanelTabId[] = [
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TAB_FINANCIAL,
  RIGHT_PANEL_TAB_RESISTANCE,
  RIGHT_PANEL_TAB_CATEGORY_SPEND,
  RIGHT_PANEL_TAB_QUANTITY,
];

// Default view shows every section pinned and merged (expanded together)
// rather than requiring the user to pop each one out individually (OP-2333).
const DEFAULT_TABS: Record<RightPanelTabId, RightPanelTabStatus> = {
  [RIGHT_PANEL_TAB_NUTRITION]: 'pinned',
  [RIGHT_PANEL_TAB_FINANCIAL]: 'pinned',
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: 'pinned',
  [RIGHT_PANEL_TAB_QUANTITY]: 'pinned',
  [RIGHT_PANEL_TAB_RESISTANCE]: 'pinned',
};

const DEFAULT_MERGED_EXPANDED: Record<RightPanelTabId, boolean> = {
  [RIGHT_PANEL_TAB_NUTRITION]: true,
  [RIGHT_PANEL_TAB_FINANCIAL]: true,
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: true,
  [RIGHT_PANEL_TAB_QUANTITY]: true,
  [RIGHT_PANEL_TAB_RESISTANCE]: true,
};

const DEFAULT_FLOATING_GEOMETRY: Record<
  RightPanelTabId,
  FloatingGeometry | null
> = {
  [RIGHT_PANEL_TAB_NUTRITION]: null,
  [RIGHT_PANEL_TAB_FINANCIAL]: null,
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: null,
  [RIGHT_PANEL_TAB_QUANTITY]: null,
  [RIGHT_PANEL_TAB_RESISTANCE]: null,
};

const DEFAULT_CHART_VISIBLE: Record<RightPanelTabId, boolean> = {
  [RIGHT_PANEL_TAB_NUTRITION]: true,
  [RIGHT_PANEL_TAB_FINANCIAL]: true,
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: true,
  [RIGHT_PANEL_TAB_QUANTITY]: true,
  [RIGHT_PANEL_TAB_RESISTANCE]: true,
};

/**
 * A saved tab order with any section added since it was saved (e.g.
 * Resistance, OP-2563) put just before its nearest following section in the
 * default order (Resistance → before Category Spend), and any no longer
 * offered dropped, so a returning user sees the new section where it belongs
 * whatever order they chose.
 */
export const withCurrentTabs = (
  saved: RightPanelTabId[]
): RightPanelTabId[] => {
  const order = saved.filter((id) => DEFAULT_TAB_ORDER.includes(id));
  DEFAULT_TAB_ORDER.forEach((id, defaultIdx) => {
    if (order.includes(id)) return;
    const nextDefault = DEFAULT_TAB_ORDER.slice(defaultIdx + 1).find((next) =>
      order.includes(next)
    );
    order.splice(
      nextDefault ? order.indexOf(nextDefault) : order.length,
      0,
      id
    );
  });
  return order;
};

/** Persisted fields (everything except floatingTabIds and analysisSnapshot). */
type PersistedRightPanelState = Omit<
  RightPanelState,
  'floatingTabIds' | 'analysisSnapshot'
>;

/**
 * The tabs layout to save to the browser. While Insights or Diagnostics has
 * force-collapsed the panel, that's the layout from before the collapse — as
 * long as the user hasn't pinned or floated anything since, the same rule
 * leaveAnalysisCollapse uses. Saving the collapsed layout instead meant a
 * reload (or a fresh visit) after Insights opened Planning with the panel
 * shut, because the snapshot that would have restored it isn't saved.
 */
export const getPersistableRightPanelLayout = (
  rightPanel: RightPanelState
): Pick<RightPanelState, 'tabs' | 'activePinnedTabId'> => {
  const snapshot = rightPanel.analysisSnapshot;
  const isStillCollapsed =
    rightPanel.floatingTabIds.length === 0 &&
    rightPanel.tabOrder.every((id) => rightPanel.tabs[id] === 'collapsed');
  return snapshot && isStillCollapsed
    ? { tabs: snapshot.tabs, activePinnedTabId: snapshot.activePinnedTabId }
    : { tabs: rightPanel.tabs, activePinnedTabId: rightPanel.activePinnedTabId };
};

/**
 * Floating tabs cannot be restored on page load (the panel is gone).
 * Convert any persisted 'floating' status back to 'collapsed'.
 */
const sanitizeTabs = (
  tabs: Record<RightPanelTabId, RightPanelTabStatus>
): Record<RightPanelTabId, RightPanelTabStatus> => {
  const result = { ...tabs };
  (Object.keys(result) as RightPanelTabId[]).forEach((id) => {
    if (result[id] === 'floating') result[id] = 'collapsed';
  });
  return result;
};

/**
 * Load persisted right panel state from localStorage at slice initialisation.
 * floatingTabIds is always reset to [] on page load.
 */
const loadRightPanelFromStorage = (): PersistedRightPanelState => {
  try {
    const raw = localStorage.getItem(RIGHT_PANEL_STATE_STORAGE_KEY);
    if (!raw) return buildDefaultRightPanel();
    const parsed = JSON.parse(raw) as Partial<PersistedRightPanelState>;
    // Defaults first, so a section added since the layout was saved gets its
    // default state rather than none (OP-2563).
    return {
      tabs: sanitizeTabs({ ...DEFAULT_TABS, ...parsed.tabs }),
      tabOrder: withCurrentTabs(parsed.tabOrder ?? DEFAULT_TAB_ORDER),
      activePinnedTabId: parsed.activePinnedTabId ?? null,
      isMerged: parsed.isMerged ?? false,
      mergedSectionExpanded: {
        ...DEFAULT_MERGED_EXPANDED,
        ...parsed.mergedSectionExpanded,
      },
      floatingGeometry: {
        ...DEFAULT_FLOATING_GEOMETRY,
        ...parsed.floatingGeometry,
      },
      chartVisible: { ...DEFAULT_CHART_VISIBLE, ...parsed.chartVisible },
      categoryFilter: parsed.categoryFilter ?? InputTaskCategory.Plan,
      // Before OP-2585 an open calculator was saved as "limeMate" — the
      // only one then — so it reopens as Calculators → LimeMate.
      activeTool:
        parsed.activeTool === RIGHT_PANEL_TOOL_CALCULATORS ||
        (parsed.activeTool as string) === RIGHT_PANEL_TOOL_LEGACY_LIME_MATE
          ? RIGHT_PANEL_TOOL_CALCULATORS
          : RIGHT_PANEL_TOOL_LIVE_TOTALS,
      activeCalculator: isCalculatorId(parsed.activeCalculator)
        ? parsed.activeCalculator
        : DEFAULT_CALCULATOR_ID,
    };
  } catch {
    return buildDefaultRightPanel();
  }
};

const buildDefaultRightPanel = (): PersistedRightPanelState => ({
  tabs: { ...DEFAULT_TABS },
  tabOrder: [...DEFAULT_TAB_ORDER],
  activePinnedTabId: DEFAULT_TAB_ORDER[0],
  isMerged: true,
  mergedSectionExpanded: { ...DEFAULT_MERGED_EXPANDED },
  floatingGeometry: { ...DEFAULT_FLOATING_GEOMETRY },
  chartVisible: { ...DEFAULT_CHART_VISIBLE },
  categoryFilter: InputTaskCategory.Plan,
  activeTool: RIGHT_PANEL_TOOL_LIVE_TOTALS,
  activeCalculator: DEFAULT_CALCULATOR_ID,
});

export type SummaryInputView =
  | typeof SUMMARY_INPUT_VIEW_TABLE
  | typeof SUMMARY_INPUT_VIEW_CHART;

/** Load the persisted Input summaries view; anything unrecognised falls back to table. */
const loadSummaryInputView = (): SummaryInputView => {
  try {
    return localStorage.getItem(SUMMARY_INPUT_VIEW_STORAGE_KEY) ===
      SUMMARY_INPUT_VIEW_CHART
      ? SUMMARY_INPUT_VIEW_CHART
      : SUMMARY_INPUT_VIEW_TABLE;
  } catch {
    return SUMMARY_INPUT_VIEW_TABLE;
  }
};

// ── Slice ────────────────────────────────────────────────────────────────────

export interface InsightsState {
  activeTab: InsightsTab;
  activeLens: InsightsLens;
  /** Planning's lens for the current scope, or null where Planning has none (OP-2549). */
  planningLens: PlanningLens | null;
  summaryEntityCategory: SummaryEntityCategory;
  summaryDataMetric: SummaryDataMetric;
  summaryTemporalLayout: SummaryTemporalLayout;
  /** Summary lens data basis — shared by every basis-aware Summary section (Cost by Crop, Input summaries, Monthly breakdown, and their charts). */
  summaryDataBasis: SummaryDataBasis;
  /** Whether the summary-scope hint banner is visible. Persisted to localStorage. */
  summaryHintVisible: boolean;
  /** Input summaries view (OP-2394) — every section shows its table or its chart, never both. Defaults to table. Persisted to localStorage. */
  summaryInputView: SummaryInputView;
  grossMarginCategory: InputTaskCategory;
  analysisCategory: InputTaskCategory;
  analysisPriceScenario: AnalysisPriceScenario;
  analysisProductionScenario: AnalysisProductionScenario;
  analysisShowPerHa: boolean;
  analysisHiddenColumns: AnalysisColumnKey[];
  analysisSummaryMode: AnalysisSummaryMode;
  analysisWeightAverageByHa: boolean;
  analysisGroupBy: AnalysisGroupBy;
  cashflowCategory: InputTaskCategory;
  cashflowPeriod: CashflowPeriod;
  cashflowYearMode: CashflowYearMode;
  cashflowDetailLevel: CashflowDetailLevel;
  /** Nutrition lens (OP-2508) — which task category feeds applied and removed figures. Session-only. */
  nutritionCategory: InputTaskCategory;
  /** Nutrition lens — which seasons the audit covers, ending at the tree year. Session-only. */
  nutritionSeasonRange: NutritionSeasonRange;
  /** Nutrition lens — which planned yield removal is worked out from (Plan mode). Session-only. */
  nutritionYieldScenario: AnalysisProductionScenario;
  /** Nutrition lens — nutrients shown as columns in the farm/org balance grid, in display order. Session-only. */
  nutritionNutrients: NutrientKey[];
  nutritionFarmView: NutritionFarmView;
  /** Nutrition lens farm/org grid — paddocks by farm, or plans by crop. Session-only. */
  nutritionGroupBy: NutritionGroupBy;
  /** Nutrition farm grid — area and yield beside each year's crop. Session-only. */
  nutritionShowCropDetails: boolean;
  /**
   * Nutrition lens rate saves (OP-2508), for the workspace's save status
   * label: how many are in flight, whether any has gone through this visit,
   * the changes that failed (kept until retried successfully), and a counter
   * the label's Retry bumps for the lens to resend them. Session-only.
   */
  nutritionSave: NutritionSaveState;
  /** Nutrition lens — the one nutrient the season-by-season farm view shows. Session-only. */
  nutritionTrendNutrient: NutrientKey;
  /** Rotation planner lens (OP-2501) — whether past years show Plan or Actual yield and sowing. This year and later always show Plan. Session-only. */
  rotationPastCategory: InputTaskCategory;
  /** Rotation planner — details shown in each cell. Session-only. */
  rotationPlannerDetails: RotationCellDetails;
  rightPanel: RightPanelState;
  farmDiagnosticsLens: FarmDiagnosticsLens;
  /** Keyed by paddockId, then crop-year. Session-only — see FarmDiagnosticsYearOverride. */
  farmDiagnosticsOverrides: Record<string, Record<number, FarmDiagnosticsYearOverride>>;
  /** Keyed by farmId, then crop-year — farm-level, not paddock-level. Remembered in this browser — loaded at start-up, written by useFarmYearInputActions. */
  farmYearInputs: Record<string, Record<number, FarmYearInput>>;
  /** Keyed by paddockId, then crop-year — the Yearly Overview's Plan/Actual choice. A year with no entry uses its row's default. In Redux (not view state) so the plan preview can be handed it (OP-2490). Session-only. */
  farmDiagnosticsCategory: Record<string, Record<number, InputTaskCategory>>;
  /** Shared across the three per-paddock Farm Diagnostics lenses (WUE Trend / Financial Efficiency / Bio vs Financial). Session-only. */
  farmDiagnosticsTimeRange: FarmDiagnosticsTimeRange;
  /** Independent time window for the Rotation Efficiency lens (Farm/Org-level paddock comparison) — deliberately not shared with farmDiagnosticsTimeRange above, since the two operate at different scopes and syncing them would cause confusing cross-talk. Session-only. */
  rotationTimeRange: FarmDiagnosticsTimeRange;
  /**
   * Keyed by farmId — whether that farm's "Farm-Level Inputs" panel
   * (rainfall/practice) is expanded. Lives in Redux rather than the panel's
   * own local state so it survives the panel unmounting and remounting when
   * the user switches between Farm Diagnostics lenses (the panel is a
   * different component instance per lens branch, so local state would
   * otherwise reset every time). Absent key defaults to expanded.
   * Session-only.
   */
  farmYearInputsExpanded: Record<string, boolean>;
}

const initialState: InsightsState = {
  activeTab: INPUT_DOC_TAB_PLANNING,
  activeLens: INPUT_DOC_LENS_SUMMARY_2,
  planningLens: null,
  summaryEntityCategory: SUMMARY_ENTITY_ALL,
  summaryDataMetric: SUMMARY_METRIC_QUANTITY,
  summaryTemporalLayout: SUMMARY_TEMPORAL_FLAT,
  summaryDataBasis: SUMMARY_DATA_BASIS_PLAN,
  summaryHintVisible:
    localStorage.getItem(SUMMARY_SCOPE_HINT_STORAGE_KEY) !== 'false',
  summaryInputView: loadSummaryInputView(),
  grossMarginCategory: InputTaskCategory.Plan,
  analysisCategory: InputTaskCategory.Plan,
  analysisPriceScenario: ANALYSIS_PRICE_EXPECTED,
  analysisProductionScenario: ANALYSIS_PRODUCTION_EXPECTED,
  analysisShowPerHa: true,
  // Default visible set: Area, Crop, Variety, Income, Total Costs, Gross
  // Margin, Cost % Income — everything else starts hidden.
  analysisHiddenColumns: [
    'farmName',
    'startDate',
    'price',
    'yield',
    'production',
    SUMMARY_ENTITY_SEED,
    SUMMARY_ENTITY_SOIL_INPUT,
    SUMMARY_ENTITY_PLANT_PROTECTION,
    SUMMARY_ENTITY_BIOSTIMULANT,
    SUMMARY_ENTITY_OPERATION,
  ],
  analysisSummaryMode: ANALYSIS_SUMMARY_TOTAL,
  analysisWeightAverageByHa: false,
  analysisGroupBy: ANALYSIS_GROUP_BY_NONE,
  cashflowCategory: InputTaskCategory.Plan,
  cashflowPeriod: CASHFLOW_PERIOD_MONTH,
  cashflowYearMode: CASHFLOW_YEAR_CALENDAR,
  cashflowDetailLevel: CASHFLOW_DETAIL_CATEGORIES,
  // Setting the nutrition program is a planning job, so Plan is the default.
  nutritionCategory: InputTaskCategory.Plan,
  nutritionSeasonRange: FARM_DIAGNOSTICS_TIME_RANGE_LAST_3,
  nutritionYieldScenario: ANALYSIS_PRODUCTION_EXPECTED,
  nutritionNutrients: ['nutrientN', 'nutrientP', 'nutrientK', 'nutrientS'],
  nutritionFarmView: NUTRITION_FARM_VIEW_BALANCE,
  nutritionGroupBy: NUTRITION_GROUP_BY_FARM,
  // Area and yield off, as they make the grid busy.
  nutritionShowCropDetails: false,
  // Past years default to what actually happened — the history planned from.
  rotationPastCategory: InputTaskCategory.Actual,
  // Yield off by default, so more of the farm fits on screen.
  rotationPlannerDetails: {
    area: true,
    variety: true,
    production: false,
    sowing: true,
  },
  nutritionSave: {
    pendingCount: 0,
    hasSaved: false,
    failed: [],
    retryRequest: 0,
  },
  nutritionTrendNutrient: 'nutrientN',
  rightPanel: {
    ...loadRightPanelFromStorage(),
    floatingTabIds: [],
    analysisSnapshot: null,
  },
  farmDiagnosticsLens: FARM_DIAGNOSTICS_LENS_WUE_TREND,
  farmDiagnosticsOverrides: {},
  farmYearInputs: loadFarmYearInputs(),
  farmDiagnosticsCategory: {},
  // "All years" as a default could span decades of rows in the Yearly
  // Overview table, pushing the actual trend charts far down the page (or
  // off it) on first load — Last 5 years is enough history for a rolling
  // average/trend to mean something while keeping the charts in view.
  farmDiagnosticsTimeRange: {
    mode: FARM_DIAGNOSTICS_TIME_RANGE_LAST_5,
    customStartYear: null,
    customEndYear: null,
  },
  // Matches farmDiagnosticsTimeRange's default above — "All years" could
  // span too much history for a fair paddock comparison either way.
  rotationTimeRange: {
    mode: FARM_DIAGNOSTICS_TIME_RANGE_LAST_5,
    customStartYear: null,
    customEndYear: null,
  },
  farmYearInputsExpanded: {},
};

const insightsSlice = createSlice({
  name: 'insights',
  initialState,
  reducers: {
    setInsightsActiveTab: (state, action: PayloadAction<InsightsTab>) => {
      state.activeTab = action.payload;
    },
    setInsightsActiveLens: (state, action: PayloadAction<InsightsLens>) => {
      state.activeLens = action.payload;
    },
    setSummaryEntityCategory: (
      state,
      action: PayloadAction<SummaryEntityCategory>
    ) => {
      state.summaryEntityCategory = action.payload;
    },
    setSummaryDataMetric: (state, action: PayloadAction<SummaryDataMetric>) => {
      state.summaryDataMetric = action.payload;
    },
    setSummaryTemporalLayout: (
      state,
      action: PayloadAction<SummaryTemporalLayout>
    ) => {
      state.summaryTemporalLayout = action.payload;
    },
    setSummaryDataBasis: (state, action: PayloadAction<SummaryDataBasis>) => {
      state.summaryDataBasis = action.payload;
    },
    setGrossMarginCategory: (
      state,
      action: PayloadAction<InputTaskCategory>
    ) => {
      state.grossMarginCategory = action.payload;
    },
    setNutritionCategory: (
      state,
      action: PayloadAction<InputTaskCategory>
    ) => {
      state.nutritionCategory = action.payload;
    },
    setRotationPastCategory: (
      state,
      action: PayloadAction<InputTaskCategory>
    ) => {
      state.rotationPastCategory = action.payload;
    },
    setRotationPlannerDetails: (
      state,
      action: PayloadAction<RotationCellDetails>
    ) => {
      state.rotationPlannerDetails = action.payload;
    },
    setNutritionSeasonRange: (
      state,
      action: PayloadAction<NutritionSeasonRange>
    ) => {
      state.nutritionSeasonRange = action.payload;
    },
    setNutritionYieldScenario: (
      state,
      action: PayloadAction<AnalysisProductionScenario>
    ) => {
      state.nutritionYieldScenario = action.payload;
    },
    setNutritionNutrients: (state, action: PayloadAction<NutrientKey[]>) => {
      state.nutritionNutrients = action.payload;
    },
    setNutritionFarmView: (
      state,
      action: PayloadAction<NutritionFarmView>
    ) => {
      state.nutritionFarmView = action.payload;
    },
    setNutritionGroupBy: (state, action: PayloadAction<NutritionGroupBy>) => {
      state.nutritionGroupBy = action.payload;
    },
    setNutritionShowCropDetails: (state, action: PayloadAction<boolean>) => {
      state.nutritionShowCropDetails = action.payload;
    },
    nutritionSaveStarted: (state) => {
      state.nutritionSave.pendingCount += 1;
    },
    /** A change saved — clears any earlier failure for the same product line. */
    nutritionSaveSucceeded: (state, action: PayloadAction<string>) => {
      const save = state.nutritionSave;
      save.pendingCount = Math.max(0, save.pendingCount - 1);
      save.hasSaved = true;
      save.failed = save.failed.filter((f) => f.key !== action.payload);
    },
    /** A change failed — kept (one per key, latest wins) until a retry succeeds. */
    nutritionSaveFailed: (
      state,
      action: PayloadAction<NutritionFailedSave>
    ) => {
      const save = state.nutritionSave;
      save.pendingCount = Math.max(0, save.pendingCount - 1);
      save.failed = [
        ...save.failed.filter((f) => f.key !== action.payload.key),
        action.payload,
      ];
    },
    nutritionRetryRequested: (state) => {
      state.nutritionSave.retryRequest += 1;
    },
    setNutritionTrendNutrient: (
      state,
      action: PayloadAction<NutrientKey>
    ) => {
      state.nutritionTrendNutrient = action.payload;
    },
    setAnalysisCategory: (state, action: PayloadAction<InputTaskCategory>) => {
      state.analysisCategory = action.payload;
    },
    setAnalysisPriceScenario: (
      state,
      action: PayloadAction<AnalysisPriceScenario>
    ) => {
      state.analysisPriceScenario = action.payload;
    },
    setAnalysisProductionScenario: (
      state,
      action: PayloadAction<AnalysisProductionScenario>
    ) => {
      state.analysisProductionScenario = action.payload;
    },
    setAnalysisShowPerHa: (state, action: PayloadAction<boolean>) => {
      state.analysisShowPerHa = action.payload;
    },
    toggleAnalysisColumn: (state, action: PayloadAction<AnalysisColumnKey>) => {
      const column = action.payload;
      state.analysisHiddenColumns = state.analysisHiddenColumns.includes(column)
        ? state.analysisHiddenColumns.filter((c) => c !== column)
        : [...state.analysisHiddenColumns, column];
    },
    setAnalysisHiddenColumns: (
      state,
      action: PayloadAction<AnalysisColumnKey[]>
    ) => {
      state.analysisHiddenColumns = action.payload;
    },
    setAnalysisSummaryMode: (
      state,
      action: PayloadAction<AnalysisSummaryMode>
    ) => {
      state.analysisSummaryMode = action.payload;
    },
    setAnalysisWeightAverageByHa: (state, action: PayloadAction<boolean>) => {
      state.analysisWeightAverageByHa = action.payload;
    },
    setAnalysisGroupBy: (state, action: PayloadAction<AnalysisGroupBy>) => {
      state.analysisGroupBy = action.payload;
    },
    setCashflowCategory: (state, action: PayloadAction<InputTaskCategory>) => {
      state.cashflowCategory = action.payload;
    },
    setCashflowPeriod: (state, action: PayloadAction<CashflowPeriod>) => {
      state.cashflowPeriod = action.payload;
    },
    setCashflowYearMode: (state, action: PayloadAction<CashflowYearMode>) => {
      state.cashflowYearMode = action.payload;
    },
    setCashflowDetailLevel: (
      state,
      action: PayloadAction<CashflowDetailLevel>
    ) => {
      state.cashflowDetailLevel = action.payload;
    },
    setSummaryHintVisible: (state, action: PayloadAction<boolean>) => {
      state.summaryHintVisible = action.payload;
    },
    setSummaryInputView: (state, action: PayloadAction<SummaryInputView>) => {
      state.summaryInputView = action.payload;
    },
    resetInsights: (state) => {
      // Reset session-specific state (tab, lens, analysis settings).
      // summaryHintVisible, summaryInputView and rightPanel are persistent UI preferences — they survive org switches.
      // farmYearInputs is remembered in this browser and keyed by farm, so it survives too.
      const { summaryHintVisible, summaryInputView, rightPanel, farmYearInputs } = state;
      Object.assign(state, initialState);
      state.farmYearInputs = farmYearInputs;
      state.summaryHintVisible = summaryHintVisible;
      state.summaryInputView = summaryInputView;
      state.rightPanel = rightPanel;
    },

    // ── Farm Diagnostics actions ─────────────────────────────────────────────

    setFarmDiagnosticsLens: (
      state,
      action: PayloadAction<FarmDiagnosticsLens>
    ) => {
      state.farmDiagnosticsLens = action.payload;
    },

    setPlanningLens: (state, action: PayloadAction<PlanningLens | null>) => {
      state.planningLens = action.payload;
    },

    setFarmDiagnosticsYieldOverride: (
      state,
      action: PayloadAction<{ paddockId: string; year: number; value: number }>
    ) => {
      const { paddockId, year, value } = action.payload;
      const paddockOverrides = (state.farmDiagnosticsOverrides[paddockId] ??= {});
      const yearOverride = (paddockOverrides[year] ??= {});
      yearOverride.yieldOverride = value;
    },

    setFarmDiagnosticsCostOverride: (
      state,
      action: PayloadAction<{
        paddockId: string;
        year: number;
        category: SummaryEntityCategory;
        value: number;
      }>
    ) => {
      const { paddockId, year, category, value } = action.payload;
      const paddockOverrides = (state.farmDiagnosticsOverrides[paddockId] ??= {});
      const yearOverride = (paddockOverrides[year] ??= {});
      const costOverrides = (yearOverride.costOverrides ??= {});
      costOverrides[category] = value;
    },

    /** Resets one paddock-year's overrides back to Epic 1's own figures. */
    clearFarmDiagnosticsYearOverrides: (
      state,
      action: PayloadAction<{ paddockId: string; year: number }>
    ) => {
      const { paddockId, year } = action.payload;
      const paddockOverrides = state.farmDiagnosticsOverrides[paddockId];
      if (paddockOverrides) {
        delete paddockOverrides[year];
      }
    },

    /** Resets every year's overrides for one paddock back to Epic 1's own figures. */
    clearAllFarmDiagnosticsOverridesForPaddock: (
      state,
      action: PayloadAction<{ paddockId: string }>
    ) => {
      delete state.farmDiagnosticsOverrides[action.payload.paddockId];
    },

    setFarmYearRainfall: (
      state,
      action: PayloadAction<{
        farmId: string;
        year: number;
        rainfallMm: number | null;
      }>
    ) => {
      const { farmId, year, rainfallMm } = action.payload;
      const farmYears = (state.farmYearInputs[farmId] ??= {});
      const yearInput = (farmYears[year] ??= { rainfallMm: null, practiceTags: [] });
      yearInput.rainfallMm = rainfallMm;
    },

    /** Replaces one farm-year's full practice-tag set — the caller (FarmYearInputsPanel) resolves mutual-exclusivity via resolvePracticeTagsAfterChange before dispatching. */
    setFarmYearPracticeTags: (
      state,
      action: PayloadAction<{ farmId: string; year: number; tags: PracticeTag[] }>
    ) => {
      const { farmId, year, tags } = action.payload;
      const farmYears = (state.farmYearInputs[farmId] ??= {});
      const yearInput = (farmYears[year] ??= { rainfallMm: null, practiceTags: [] });
      yearInput.practiceTags = tags;
    },

    setFarmDiagnosticsCategory: (
      state,
      action: PayloadAction<{
        paddockId: string;
        year: number;
        category: InputTaskCategory;
      }>
    ) => {
      const { paddockId, year, category } = action.payload;
      (state.farmDiagnosticsCategory[paddockId] ??= {})[year] = category;
    },

    /** Loads the Yearly Overview handed over by the workspace into a freshly opened plan preview tab (OP-2490). */
    hydrateFarmDiagnosticsSnapshot: (
      state,
      action: PayloadAction<FarmDiagnosticsSnapshot>
    ) => {
      Object.assign(state, action.payload);
    },

    /** Toggles/sets whether one farm's Farm-Level Inputs panel is expanded — kept in Redux (not the panel's own local state) so it survives the panel remounting when the active Farm Diagnostics lens changes. */
    setFarmYearInputsExpanded: (
      state,
      action: PayloadAction<{ farmId: string; expanded: boolean }>
    ) => {
      state.farmYearInputsExpanded[action.payload.farmId] = action.payload.expanded;
    },

    /** Replaces the whole time-range selection for the three per-paddock lenses. */
    setFarmDiagnosticsTimeRange: (
      state,
      action: PayloadAction<FarmDiagnosticsTimeRange>
    ) => {
      state.farmDiagnosticsTimeRange = action.payload;
    },

    /** Replaces the whole time-range selection for the Rotation Efficiency lens — independent of setFarmDiagnosticsTimeRange above. */
    setRotationTimeRange: (
      state,
      action: PayloadAction<FarmDiagnosticsTimeRange>
    ) => {
      state.rotationTimeRange = action.payload;
    },

    // ── Right Panel actions ────────────────────────────────────────────────

    /**
     * Float a tab. Multiple tabs can float simultaneously.
     * No-op if the tab is already floating.
     */
    floatRightPanelTab: (state, action: PayloadAction<RightPanelTabId>) => {
      const tabId = action.payload;
      if (!state.rightPanel.floatingTabIds.includes(tabId)) {
        state.rightPanel.floatingTabIds.push(tabId);
      }
      state.rightPanel.tabs[tabId] = 'floating';
    },

    /**
     * Pin a tab. Removes it from floatingTabIds if it was floating.
     * Sets activePinnedTabId if no other tab is pinned yet.
     */
    pinRightPanelTab: (state, action: PayloadAction<RightPanelTabId>) => {
      const tabId = action.payload;
      state.rightPanel.floatingTabIds = state.rightPanel.floatingTabIds.filter(
        (id) => id !== tabId
      );
      state.rightPanel.tabs[tabId] = 'pinned';
      if (!state.rightPanel.activePinnedTabId) {
        state.rightPanel.activePinnedTabId = tabId;
      }
    },

    /**
     * Collapse a tab (unpin or close floating).
     * Re-assigns activePinnedTabId to the next pinned tab if needed.
     */
    collapseRightPanelTab: (state, action: PayloadAction<RightPanelTabId>) => {
      const tabId = action.payload;
      state.rightPanel.tabs[tabId] = 'collapsed';

      state.rightPanel.floatingTabIds = state.rightPanel.floatingTabIds.filter(
        (id) => id !== tabId
      );

      if (state.rightPanel.activePinnedTabId === tabId) {
        const nextPinned = state.rightPanel.tabOrder.find(
          (id) => id !== tabId && state.rightPanel.tabs[id] === 'pinned'
        );
        state.rightPanel.activePinnedTabId = nextPinned ?? null;
      }
    },

    /** Switch the visible tab in the pinned panel (when 2+ pinned, not merged). */
    setRightPanelActivePinnedTab: (
      state,
      action: PayloadAction<RightPanelTabId>
    ) => {
      state.rightPanel.activePinnedTabId = action.payload;
    },

    /** Toggle merge mode on/off. */
    setRightPanelMerged: (state, action: PayloadAction<boolean>) => {
      state.rightPanel.isMerged = action.payload;
    },

    /** Toggle expand/collapse of a section in merge mode. */
    toggleRightPanelSectionExpanded: (
      state,
      action: PayloadAction<RightPanelTabId>
    ) => {
      state.rightPanel.mergedSectionExpanded[action.payload] =
        !state.rightPanel.mergedSectionExpanded[action.payload];
    },

    /** Update tab order (drag reorder in tab control or ↑↓ in merge mode). */
    setRightPanelTabOrder: (
      state,
      action: PayloadAction<RightPanelTabId[]>
    ) => {
      state.rightPanel.tabOrder = action.payload;
    },

    /** Pin every tab. Clears all floating state. */
    pinAllRightPanelTabs: (state) => {
      state.rightPanel.tabOrder.forEach((id) => {
        state.rightPanel.tabs[id] = 'pinned';
      });
      state.rightPanel.floatingTabIds = [];
      if (!state.rightPanel.activePinnedTabId) {
        state.rightPanel.activePinnedTabId =
          state.rightPanel.tabOrder[0] ?? null;
      }
    },

    /**
     * Collapse every tab. Clears all floating and active pinned state, and
     * closes any open calculator so the pinned panel actually collapses.
     */
    collapseAllRightPanelTabs: (state) => {
      state.rightPanel.tabOrder.forEach((id) => {
        state.rightPanel.tabs[id] = 'collapsed';
      });
      state.rightPanel.floatingTabIds = [];
      state.rightPanel.activePinnedTabId = null;
      state.rightPanel.activeTool = RIGHT_PANEL_TOOL_LIVE_TOTALS;
    },

    /**
     * Collapse every tab AND reset all saved floating geometry back to null.
     * The next time a tab is floated it will reopen at its default position.
     */
    resetAllRightPanelTabs: (state) => {
      state.rightPanel.tabOrder.forEach((id) => {
        state.rightPanel.tabs[id] = 'collapsed';
        state.rightPanel.floatingGeometry[id] = null;
      });
      state.rightPanel.floatingTabIds = [];
      state.rightPanel.activePinnedTabId = null;
      state.rightPanel.activeTool = RIGHT_PANEL_TOOL_LIVE_TOTALS;
    },

    /**
     * Move a floating tab to the end of floatingTabIds so it renders on top.
     * No-op if the tab is already the topmost panel.
     */
    bringRightPanelTabToFront: (
      state,
      action: PayloadAction<RightPanelTabId>
    ) => {
      const tabId = action.payload;
      const ids = state.rightPanel.floatingTabIds;
      const idx = ids.indexOf(tabId);
      if (idx !== -1 && idx < ids.length - 1) {
        ids.splice(idx, 1);
        ids.push(tabId);
      }
    },

    /** Persist the last-known geometry for a floating tab (all values in rem). */
    setRightPanelFloatingGeometry: (
      state,
      action: PayloadAction<{
        tabId: RightPanelTabId;
        geometry: FloatingGeometry;
      }>
    ) => {
      state.rightPanel.floatingGeometry[action.payload.tabId] =
        action.payload.geometry;
    },

    /** Show or hide the chart section for a specific tab. */
    setRightPanelChartVisible: (
      state,
      action: PayloadAction<{ tabId: RightPanelTabId; visible: boolean }>
    ) => {
      state.rightPanel.chartVisible[action.payload.tabId] =
        action.payload.visible;
    },

    /** Shared Plan/Actual selection for every tab's narrow-width fallback (OP-2333). */
    setRightPanelCategoryFilter: (
      state,
      action: PayloadAction<InputTaskCategory>
    ) => {
      state.rightPanel.categoryFilter = action.payload;
    },

    /**
     * Switch the pinned panel between live totals and a calculator (OP-2383).
     * Returning to live totals with nothing pinned re-pins every tab, so the
     * panel doesn't vanish from under the user's selector click.
     */
    setRightPanelActiveTool: (state, action: PayloadAction<RightPanelTool>) => {
      state.rightPanel.activeTool = action.payload;
      if (action.payload !== RIGHT_PANEL_TOOL_LIVE_TOTALS) return;
      const nonePinned = state.rightPanel.tabOrder.every(
        (id) => state.rightPanel.tabs[id] !== 'pinned'
      );
      if (!nonePinned) return;
      state.rightPanel.tabOrder.forEach((id) => {
        state.rightPanel.tabs[id] = 'pinned';
      });
      state.rightPanel.floatingTabIds = [];
      state.rightPanel.activePinnedTabId = state.rightPanel.tabOrder[0] ?? null;
    },

    /** Choose the calculator the Calculators tool shows (OP-2585). */
    setRightPanelActiveCalculator: (
      state,
      action: PayloadAction<CalculatorId>
    ) => {
      state.rightPanel.activeCalculator = action.payload;
    },

    /**
     * Close the open calculator (OP-2383). Returns to live totals without
     * re-pinning anything — if no tab is pinned, the pinned panel collapses.
     */
    closeRightPanelTool: (state) => {
      state.rightPanel.activeTool = RIGHT_PANEL_TOOL_LIVE_TOTALS;
    },

    /**
     * Snapshot the current pin/float layout and force-collapse every tab.
     * Called when the Analysis lens becomes active (OP-2333) — Analysis
     * already surfaces the same cost/rate figures the Live Panel shows, so
     * the panel isn't needed by default there, though the user can still
     * manually re-pin/float tabs while viewing it. No-op if a snapshot is
     * already pending (re-entering Analysis without having left it).
     */
    enterAnalysisCollapse: (state) => {
      if (state.rightPanel.analysisSnapshot) return;
      state.rightPanel.analysisSnapshot = {
        tabs: { ...state.rightPanel.tabs },
        activePinnedTabId: state.rightPanel.activePinnedTabId,
        floatingTabIds: [...state.rightPanel.floatingTabIds],
      };
      state.rightPanel.tabOrder.forEach((id) => {
        state.rightPanel.tabs[id] = 'collapsed';
      });
      state.rightPanel.floatingTabIds = [];
      state.rightPanel.activePinnedTabId = null;
    },

    /**
     * Restore the layout saved by enterAnalysisCollapse, but only if the
     * user made no manual pin/float changes while Analysis was active (i.e.
     * every tab is still collapsed) — an explicit action while viewing
     * Analysis is respected over the pre-Analysis snapshot. Always clears
     * the pending snapshot. No-op if there is nothing to restore.
     */
    leaveAnalysisCollapse: (state) => {
      const snapshot = state.rightPanel.analysisSnapshot;
      if (!snapshot) return;
      const stillAllCollapsed =
        state.rightPanel.floatingTabIds.length === 0 &&
        state.rightPanel.tabOrder.every(
          (id) => state.rightPanel.tabs[id] === 'collapsed'
        );
      if (stillAllCollapsed) {
        state.rightPanel.tabs = snapshot.tabs;
        state.rightPanel.activePinnedTabId = snapshot.activePinnedTabId;
        state.rightPanel.floatingTabIds = snapshot.floatingTabIds;
      }
      state.rightPanel.analysisSnapshot = null;
    },
  },
});

export const {
  setInsightsActiveTab,
  setInsightsActiveLens,
  setSummaryEntityCategory,
  setSummaryDataMetric,
  setSummaryTemporalLayout,
  setSummaryDataBasis,
  setGrossMarginCategory,
  setNutritionCategory,
  setRotationPastCategory,
  setRotationPlannerDetails,
  setNutritionSeasonRange,
  setNutritionYieldScenario,
  setNutritionNutrients,
  setNutritionFarmView,
  setNutritionGroupBy,
  setNutritionTrendNutrient,
  setNutritionShowCropDetails,
  nutritionSaveStarted,
  nutritionSaveSucceeded,
  nutritionSaveFailed,
  nutritionRetryRequested,
  setAnalysisCategory,
  setAnalysisPriceScenario,
  setAnalysisProductionScenario,
  setAnalysisShowPerHa,
  toggleAnalysisColumn,
  setAnalysisHiddenColumns,
  setAnalysisSummaryMode,
  setAnalysisWeightAverageByHa,
  setAnalysisGroupBy,
  setCashflowCategory,
  setCashflowPeriod,
  setCashflowYearMode,
  setCashflowDetailLevel,
  setSummaryHintVisible,
  setSummaryInputView,
  resetInsights,
  setFarmDiagnosticsLens,
  setPlanningLens,
  setFarmDiagnosticsYieldOverride,
  setFarmDiagnosticsCostOverride,
  clearFarmDiagnosticsYearOverrides,
  clearAllFarmDiagnosticsOverridesForPaddock,
  setFarmYearRainfall,
  setFarmYearPracticeTags,
  setFarmDiagnosticsCategory,
  hydrateFarmDiagnosticsSnapshot,
  setFarmYearInputsExpanded,
  setFarmDiagnosticsTimeRange,
  setRotationTimeRange,
  floatRightPanelTab,
  pinRightPanelTab,
  collapseRightPanelTab,
  bringRightPanelTabToFront,
  setRightPanelActivePinnedTab,
  setRightPanelMerged,
  toggleRightPanelSectionExpanded,
  setRightPanelTabOrder,
  pinAllRightPanelTabs,
  collapseAllRightPanelTabs,
  resetAllRightPanelTabs,
  setRightPanelFloatingGeometry,
  setRightPanelChartVisible,
  setRightPanelCategoryFilter,
  setRightPanelActiveTool,
  setRightPanelActiveCalculator,
  closeRightPanelTool,
  enterAnalysisCollapse,
  leaveAnalysisCollapse,
} = insightsSlice.actions;

export default insightsSlice.reducer;
